import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import PatientSearch from "@/components/patient/patient-search";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { PatientCreditTransferReason, TransferPatientCreditRequest } from "@/types/billing/patient-credit-transfer";
import type { PatientSummary } from "@/types/patient";
interface SourceCredit {
  id: string;
  amount: number;
  amountRemaining: number;
}
interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  credit: SourceCredit | null;
  fromPatientId: string;
  isLoading?: boolean;
  onSubmit: (req: TransferPatientCreditRequest) => Promise<void> | void;
}
const REASON_OPTIONS: {
  value: PatientCreditTransferReason;
  label: string;
}[] = [{
  value: "GIFT",
  label: "Gift"
}, {
  value: "FAMILY_SHARE",
  label: "Family share"
}, {
  value: "ACCOUNT_MERGE",
  label: "Account merge"
}, {
  value: "CORRECTION",
  label: "Correction"
}, {
  value: "OTHER",
  label: "Other"
}];
const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const PatientCreditTransferModal: React.FC<Props> = t0 => {
  const $ = _c(52);
  const {
    isOpen,
    setIsOpen,
    credit,
    fromPatientId,
    isLoading: t1,
    onSubmit
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const remaining = credit?.amountRemaining ?? 0;
  const total = credit?.amount ?? 0;
  const [recipient, setRecipient] = useState(undefined);
  const [dollarsStr, setDollarsStr] = useState("");
  const [reason, setReason] = useState("GIFT");
  const [note, setNote] = useState("");
  let t2;
  let t3;
  if ($[0] !== isOpen) {
    t2 = () => {
      if (!isOpen) {
        setRecipient(undefined);
        setDollarsStr("");
        setReason("GIFT");
        setNote("");
      }
    };
    t3 = [isOpen];
    $[0] = isOpen;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  const parsedDollars = Number(dollarsStr);
  let t4;
  if ($[3] !== dollarsStr || $[4] !== parsedDollars) {
    t4 = dollarsStr !== "" && !Number.isNaN(parsedDollars) && parsedDollars > 0;
    $[3] = dollarsStr;
    $[4] = parsedDollars;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const isValidNumber = t4;
  let t5;
  if ($[6] !== isValidNumber || $[7] !== parsedDollars) {
    t5 = isValidNumber ? Math.round(parsedDollars * 100) : 0;
    $[6] = isValidNumber;
    $[7] = parsedDollars;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const cents = t5;
  const inBounds = isValidNumber && cents > 0 && cents <= remaining;
  const noteRequired = reason === "OTHER";
  let t6;
  if ($[9] !== note || $[10] !== noteRequired) {
    t6 = !noteRequired || note.trim().length > 0;
    $[9] = note;
    $[10] = noteRequired;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const noteOk = t6;
  const recipientOk = recipient !== undefined && recipient.id !== fromPatientId;
  const canSubmit = !isLoading && remaining > 0 && recipientOk && isValidNumber && inBounds && noteOk;
  let t7;
  if ($[12] !== canSubmit || $[13] !== cents || $[14] !== credit || $[15] !== note || $[16] !== onSubmit || $[17] !== reason || $[18] !== recipient) {
    t7 = async () => {
      if (!canSubmit || !recipient || !credit) {
        return;
      }
      const trimmedNote = note.trim();
      const base = {
        toPatientId: recipient.id,
        amount: cents
      };
      const req = reason === "OTHER" ? {
        ...base,
        reason,
        note: trimmedNote
      } : {
        ...base,
        reason,
        note: trimmedNote || undefined
      };
      await onSubmit(req);
    };
    $[12] = canSubmit;
    $[13] = cents;
    $[14] = credit;
    $[15] = note;
    $[16] = onSubmit;
    $[17] = reason;
    $[18] = recipient;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleSubmit = t7;
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DialogHeader><DialogTitle>Transfer Patient Credit</DialogTitle><DialogDescription className="mt-1 text-body leading-6">Move part of this credit balance onto another patient. No money moves in or out of the merchant — this is an internal balance move.</DialogDescription></DialogHeader>;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== dollarsStr || $[22] !== fromPatientId || $[23] !== inBounds || $[24] !== isLoading || $[25] !== isValidNumber || $[26] !== note || $[27] !== noteRequired || $[28] !== reason || $[29] !== recipient || $[30] !== remaining || $[31] !== total) {
    t9 = <DialogBody>{remaining <= 0 ? <p className="text-body text-foreground-secondary">This credit has no remaining balance to transfer.</p> : <div className="space-y-4"><div><Label htmlFor="credit-transfer-recipient" mandatory={true}>Recipient</Label><div className="mt-1"><PatientSearch patient={recipient} onPatientSelect={p => setRecipient(p)} placeholder="Search for the recipient patient" searchFilter={patients => patients.filter(p_0 => p_0.id !== fromPatientId)} showCreatePatient={false} /></div>{recipient !== undefined && recipient.id === fromPatientId && <p className="mt-1 text-caption text-status-error-text">Cannot transfer to the same patient.</p>}</div><div><div className="flex items-baseline justify-between"><Label htmlFor="credit-transfer-amount" mandatory={true}>Amount to transfer</Label><span className="text-caption text-foreground-secondary">{formatCents(remaining)} of {formatCents(total)} remaining</span></div><Input id="credit-transfer-amount" type="number" min={0.01} step={0.01} inputMode="decimal" value={dollarsStr} onChange={e => setDollarsStr(e.target.value)} disabled={isLoading} className="mt-1" hasError={isValidNumber && !inBounds} errorMessage={isValidNumber && !inBounds ? `Cannot exceed remaining balance (${formatCents(remaining)}).` : undefined} /></div><div><Label htmlFor="credit-transfer-reason" mandatory={true}>Reason</Label><Select value={reason} onValueChange={v => setReason(v as PatientCreditTransferReason)}><SelectTrigger id="credit-transfer-reason" className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{REASON_OPTIONS.map(_temp)}</SelectContent></Select></div><div><Label htmlFor="credit-transfer-note" mandatory={noteRequired}><span>Internal note{noteRequired ? <span>{""}</span> : <span>{" (optional)"}</span>}</span></Label><Textarea id="credit-transfer-note" value={note} onChange={e_0 => setNote(e_0.target.value)} placeholder="Any context you'd like to record with this transfer..." className="mt-1" rows={3} disabled={isLoading} maxLength={1000} /></div></div>}</DialogBody>;
    $[21] = dollarsStr;
    $[22] = fromPatientId;
    $[23] = inBounds;
    $[24] = isLoading;
    $[25] = isValidNumber;
    $[26] = note;
    $[27] = noteRequired;
    $[28] = reason;
    $[29] = recipient;
    $[30] = remaining;
    $[31] = total;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== setIsOpen) {
    t10 = () => setIsOpen(false);
    $[33] = setIsOpen;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== isLoading || $[36] !== t10) {
    t11 = <DialogClose asChild={true}><Button variant="secondary" className="w-fit" onClick={t10} disabled={isLoading}>Cancel</Button></DialogClose>;
    $[35] = isLoading;
    $[36] = t10;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const t12 = !canSubmit;
  let t13;
  if ($[38] !== handleSubmit || $[39] !== isLoading || $[40] !== t12) {
    t13 = <Button className="w-fit" variant="primary" onClick={handleSubmit} disabled={t12} isLoading={isLoading} loadingText="Transferring...">Transfer</Button>;
    $[38] = handleSubmit;
    $[39] = isLoading;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t11 || $[43] !== t13) {
    t14 = <DialogFooter>{t11}{t13}</DialogFooter>;
    $[42] = t11;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t14 || $[46] !== t9) {
    t15 = <DialogContent className="sm:max-w-lg">{t8}{t9}{t14}</DialogContent>;
    $[45] = t14;
    $[46] = t9;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== isOpen || $[49] !== setIsOpen || $[50] !== t15) {
    t16 = <Dialog open={isOpen} onOpenChange={setIsOpen}>{t15}</Dialog>;
    $[48] = isOpen;
    $[49] = setIsOpen;
    $[50] = t15;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  return t16;
};
export default PatientCreditTransferModal;
function _temp(opt) {
  return <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>;
}